// ===================================================
// hooks/useNavigationPreferences.js
// Hydrate + debounce-save user sidebar IA prefs (cross-device).
// ===================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/index';
import { DEFAULT_NAV_PRESET_ID } from '../config/navigation.presets';

export const NAVIGATION_PREFERENCES_CHANGED = 'navigation_preferences_changed';
export const NAV_PREFS_STORAGE_KEY = 'mb_nav_preferences_v1';

export const DOCK_MODE_ALWAYS_FIRST_3 = 'always_first_3';
export const DOCK_MODE_FAVORITES_ONLY = 'favorites_only';
export const DOCK_MODE_HIDDEN = 'hidden';

const DOCK_MODES = new Set([
  DOCK_MODE_ALWAYS_FIRST_3,
  DOCK_MODE_FAVORITES_ONLY,
  DOCK_MODE_HIDDEN,
]);

const DEFAULT_PLATFORM = {
  hotKeys: [],
  newKeys: [],
  defaultDockDisplayMode: DOCK_MODE_ALWAYS_FIRST_3,
  systemPresets: null,
};

const DEFAULT_PREFS = {
  activePresetId: DEFAULT_NAV_PRESET_ID,
  hubOrder: [],
  hiddenHubIds: [],
  hubOverrides: {},
  personalPresets: [],
  sidebarCollapsed: false,
  dockDisplayMode: DOCK_MODE_FAVORITES_ONLY,
  platform: DEFAULT_PLATFORM,
  updatedAt: null,
};

function normalizeDockMode(value, fallback = DOCK_MODE_ALWAYS_FIRST_3) {
  return DOCK_MODES.has(value) ? value : fallback;
}

function normalizePlatform(input) {
  if (!input || typeof input !== 'object') return { ...DEFAULT_PLATFORM };
  return {
    hotKeys: Array.isArray(input.hotKeys) ? input.hotKeys.filter((x) => typeof x === 'string') : [],
    newKeys: Array.isArray(input.newKeys) ? input.newKeys.filter((x) => typeof x === 'string') : [],
    defaultDockDisplayMode: normalizeDockMode(
      input.defaultDockDisplayMode,
      DEFAULT_PLATFORM.defaultDockDisplayMode
    ),
    systemPresets: Array.isArray(input.systemPresets) ? input.systemPresets : null,
  };
}

function readLocalMirror() {
  try {
    const raw = localStorage.getItem(NAV_PREFS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return normalizePrefs(parsed);
  } catch {
    return null;
  }
}

function writeLocalMirror(prefs) {
  try {
    localStorage.setItem(NAV_PREFS_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore quota */
  }
}

export function normalizePrefs(input = {}, previous = null) {
  const prevPlatform = previous?.platform;
  const hasPlatform =
    input.platform && typeof input.platform === 'object' && !Array.isArray(input.platform);
  return {
    activePresetId:
      typeof input.activePresetId === 'string' && input.activePresetId
        ? input.activePresetId
        : DEFAULT_NAV_PRESET_ID,
    hubOrder: Array.isArray(input.hubOrder) ? input.hubOrder.filter((x) => typeof x === 'string') : [],
    hiddenHubIds: Array.isArray(input.hiddenHubIds)
      ? input.hiddenHubIds.filter((x) => typeof x === 'string')
      : [],
    hubOverrides:
      input.hubOverrides && typeof input.hubOverrides === 'object' && !Array.isArray(input.hubOverrides)
        ? input.hubOverrides
        : {},
    personalPresets: Array.isArray(input.personalPresets) ? input.personalPresets : [],
    sidebarCollapsed: Boolean(input.sidebarCollapsed),
    dockDisplayMode: normalizeDockMode(input.dockDisplayMode),
    platform: hasPlatform
      ? normalizePlatform(input.platform)
      : normalizePlatform(prevPlatform || DEFAULT_PLATFORM),
    updatedAt: input.updatedAt || null,
  };
}

function emitChanged(prefs) {
  try {
    window.dispatchEvent(
      new CustomEvent(NAVIGATION_PREFERENCES_CHANGED, { detail: prefs })
    );
  } catch {
    /* ignore */
  }
}

function prefsEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.activePresetId === b.activePresetId &&
    a.sidebarCollapsed === b.sidebarCollapsed &&
    a.dockDisplayMode === b.dockDisplayMode &&
    a.updatedAt === b.updatedAt &&
    JSON.stringify(a.hubOrder) === JSON.stringify(b.hubOrder) &&
    JSON.stringify(a.hiddenHubIds) === JSON.stringify(b.hiddenHubIds) &&
    JSON.stringify(a.hubOverrides) === JSON.stringify(b.hubOverrides) &&
    JSON.stringify(a.personalPresets) === JSON.stringify(b.personalPresets) &&
    JSON.stringify(a.platform) === JSON.stringify(b.platform)
  );
}

export function useNavigationPreferences({ enabled = true } = {}) {
  const [prefs, setPrefs] = useState(() => readLocalMirror() || DEFAULT_PREFS);
  const [loading, setLoading] = useState(Boolean(enabled) && !readLocalMirror());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const saveTimer = useRef(null);
  const pendingSaveRef = useRef(null);
  const prefsRef = useRef(prefs);
  const hydrateOnce = useRef(false);
  const dirtyRef = useRef(false);
  prefsRef.current = prefs;

  const applyLocal = useCallback((next, { emit = true, markDirty = false } = {}) => {
    const normalized = normalizePrefs(next, prefsRef.current);
    if (prefsEqual(prefsRef.current, normalized)) {
      return prefsRef.current;
    }
    setPrefs(normalized);
    writeLocalMirror(normalized);
    if (markDirty) dirtyRef.current = true;
    if (emit) emitChanged(normalized);
    return normalized;
  }, []);

  const hydrate = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (!readLocalMirror()) setLoading(true);
    setError(null);
    try {
      const result = await api.users.getNavigationPreferences();
      if (result?.success && result.data) {
        if (dirtyRef.current || pendingSaveRef.current) {
          return;
        }
        const server = normalizePrefs(result.data);
        const local = prefsRef.current;
        const serverTs = server.updatedAt ? Date.parse(server.updatedAt) : 0;
        const localTs = local.updatedAt ? Date.parse(local.updatedAt) : 0;
        if (localTs && serverTs && localTs > serverTs) {
          // Keep local edits but refresh platform badges from server
          applyLocal({ ...local, platform: server.platform }, { emit: false });
          return;
        }
        applyLocal(server, { emit: false });
      }
    } catch (err) {
      setError(err?.message || 'Не удалось загрузить настройки меню');
    } finally {
      setLoading(false);
    }
  }, [enabled, applyLocal]);

  useEffect(() => {
    if (hydrateOnce.current) return undefined;
    hydrateOnce.current = true;
    hydrate();
    return undefined;
  }, [hydrate]);

  useEffect(() => {
    const onExternal = (e) => {
      if (e?.detail) {
        applyLocal(e.detail, { emit: false });
      } else {
        hydrate();
      }
    };
    window.addEventListener(NAVIGATION_PREFERENCES_CHANGED, onExternal);
    return () => window.removeEventListener(NAVIGATION_PREFERENCES_CHANGED, onExternal);
  }, [hydrate, applyLocal]);

  const persist = useCallback(
    (next, { immediate = false } = {}) => {
      const normalized = applyLocal(next, { markDirty: true });
      pendingSaveRef.current = normalized;
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      const run = async () => {
        const payload = pendingSaveRef.current || prefsRef.current;
        pendingSaveRef.current = null;
        setSaving(true);
        setError(null);
        try {
          const { platform: _p, ...toSave } = payload;
          const result = await api.users.saveNavigationPreferences(toSave);
          if (result?.success && result.data) {
            dirtyRef.current = false;
            applyLocal(result.data, { emit: false });
          }
        } catch (err) {
          setError(err?.message || 'Не удалось сохранить настройки меню');
        } finally {
          setSaving(false);
        }
      };
      if (immediate) {
        run();
      } else {
        saveTimer.current = setTimeout(run, 400);
      }
      return normalized;
    },
    [applyLocal]
  );

  useEffect(
    () => () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      const payload = pendingSaveRef.current;
      if (payload && enabled) {
        pendingSaveRef.current = null;
        try {
          const { platform: _p, ...toSave } = payload;
          api.users.saveNavigationPreferences(toSave);
        } catch {
          /* best-effort */
        }
      }
    },
    [enabled]
  );

  const patch = useCallback(
    (partial, opts) => {
      return persist({ ...prefsRef.current, ...partial }, opts);
    },
    [persist]
  );

  const setActivePreset = useCallback(
    (presetId) => {
      if (typeof presetId === 'string' && presetId.startsWith('personal:')) {
        const personalId = presetId.slice('personal:'.length);
        const personal = (prefsRef.current.personalPresets || []).find((p) => p.id === personalId);
        if (personal) {
          return patch(
            {
              activePresetId: presetId,
              hubOrder: Array.isArray(personal.hubOrder) ? personal.hubOrder : [],
              hiddenHubIds: Array.isArray(personal.hiddenHubIds) ? personal.hiddenHubIds : [],
              hubOverrides:
                personal.hubOverrides && typeof personal.hubOverrides === 'object'
                  ? personal.hubOverrides
                  : {},
              dockDisplayMode: normalizeDockMode(
                personal.dockDisplayMode,
                prefsRef.current.dockDisplayMode
              ),
            },
            { immediate: true }
          );
        }
      }
      return patch(
        {
          activePresetId: presetId,
          hubOrder: [],
          hiddenHubIds: [],
          hubOverrides: {},
        },
        { immediate: true }
      );
    },
    [patch]
  );

  const setCollapsed = useCallback(
    (collapsed) => patch({ sidebarCollapsed: Boolean(collapsed) }),
    [patch]
  );

  const setHubOrder = useCallback(
    (hubOrder) => patch({ hubOrder }),
    [patch]
  );

  const setHiddenHubIds = useCallback(
    (hiddenHubIds) => patch({ hiddenHubIds }),
    [patch]
  );

  const setDockDisplayMode = useCallback(
    (dockDisplayMode) =>
      patch({ dockDisplayMode: normalizeDockMode(dockDisplayMode) }, { immediate: true }),
    [patch]
  );

  const setHubOverride = useCallback(
    (hubId, override) => {
      const current = prefsRef.current.hubOverrides || {};
      const nextOverrides = { ...current };
      if (!override) {
        delete nextOverrides[hubId];
      } else {
        nextOverrides[hubId] = { ...(current[hubId] || {}), ...override };
      }
      return patch({ hubOverrides: nextOverrides });
    },
    [patch]
  );

  /** Replace hubOverrides wholesale (favorites sync). */
  const setHubOverrides = useCallback(
    (hubOverrides) => patch({
      hubOverrides:
        hubOverrides && typeof hubOverrides === 'object' && !Array.isArray(hubOverrides)
          ? hubOverrides
          : {},
    }),
    [patch]
  );

  const resetToDefault = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await api.users.resetNavigationPreferences();
      if (result?.success && result.data) {
        applyLocal(result.data);
      } else {
        applyLocal(DEFAULT_PREFS);
      }
    } catch (err) {
      setError(err?.message || 'Не удалось сбросить меню');
      applyLocal(DEFAULT_PREFS);
    } finally {
      setSaving(false);
    }
  }, [applyLocal]);

  const savePersonalPreset = useCallback(
    async ({ name, snapshot } = {}) => {
      setSaving(true);
      try {
        const result = await api.users.savePersonalNavPreset({
          name: name || 'Мой пресет',
          snapshot: snapshot || {
            hubIds: prefsRef.current.hubOrder?.length
              ? prefsRef.current.hubOrder
              : undefined,
            hubOrder: prefsRef.current.hubOrder,
            hiddenHubIds: prefsRef.current.hiddenHubIds,
            hubOverrides: prefsRef.current.hubOverrides,
            dockDisplayMode: prefsRef.current.dockDisplayMode,
          },
        });
        if (result?.success && result.data) {
          applyLocal(result.data);
        }
        return result;
      } finally {
        setSaving(false);
      }
    },
    [applyLocal]
  );

  const deletePersonalPreset = useCallback(
    async (presetId) => {
      setSaving(true);
      try {
        const result = await api.users.deletePersonalNavPreset(presetId);
        if (result?.success && result.data) {
          applyLocal(result.data);
        }
        return result;
      } finally {
        setSaving(false);
      }
    },
    [applyLocal]
  );

  const importSnapshot = useCallback(
    (snapshot) => {
      if (!snapshot || typeof snapshot !== 'object') {
        throw new Error('Некорректный файл пресета');
      }
      return patch(
        {
          activePresetId:
            typeof snapshot.activePresetId === 'string'
              ? snapshot.activePresetId
              : prefsRef.current.activePresetId,
          hubOrder: Array.isArray(snapshot.hubOrder) ? snapshot.hubOrder : prefsRef.current.hubOrder,
          hiddenHubIds: Array.isArray(snapshot.hiddenHubIds)
            ? snapshot.hiddenHubIds
            : prefsRef.current.hiddenHubIds,
          hubOverrides:
            snapshot.hubOverrides && typeof snapshot.hubOverrides === 'object'
              ? snapshot.hubOverrides
              : prefsRef.current.hubOverrides,
          dockDisplayMode: normalizeDockMode(
            snapshot.dockDisplayMode,
            prefsRef.current.dockDisplayMode
          ),
          personalPresets: Array.isArray(snapshot.personalPresets)
            ? snapshot.personalPresets
            : prefsRef.current.personalPresets,
        },
        { immediate: true }
      );
    },
    [patch]
  );

  const exportSnapshot = useCallback(() => {
    const p = prefsRef.current;
    return {
      version: 1,
      exportedAt: new Date().toISOString(),
      activePresetId: p.activePresetId,
      hubOrder: p.hubOrder,
      hiddenHubIds: p.hiddenHubIds,
      hubOverrides: p.hubOverrides,
      dockDisplayMode: p.dockDisplayMode,
      personalPresets: p.personalPresets,
    };
  }, []);

  return {
    prefs,
    loading,
    saving,
    error,
    hydrate,
    patch,
    persist,
    setActivePreset,
    setCollapsed,
    setHubOrder,
    setHiddenHubIds,
    setDockDisplayMode,
    setHubOverride,
    setHubOverrides,
    resetToDefault,
    savePersonalPreset,
    deletePersonalPreset,
    importSnapshot,
    exportSnapshot,
  };
}

export default useNavigationPreferences;
